/**
 * Dev Notification Form Component
 *
 * Form for creating test notifications with:
 * - Template presets for common notification types
 * - Manual field editing for custom notifications
 * - Payload JSON editor with validation
 * - Real-time character count for title/body
 * - User selection dropdowns for recipient and actor
 *
 * @module components/DevNotificationForm
 */

import { useState, useEffect } from 'react';
import type { CreateNotificationInput } from '@/hooks/graphql/useNotificationMutationsGraphQL';
import type { NotificationType, NotificationUrgency } from '@/types/notification';
import { useAdminUsersGraphQL } from '@/graphql/hooks/useAdminUsersGraphQL';
import logger from '@/utils/logger';

interface DevNotificationFormProps {
  onSubmit: (request: CreateNotificationInput) => void;
  isSubmitting: boolean;
}

// ============================================================================
// Notification Templates
// ============================================================================

interface NotificationTemplate {
  id: string;
  name: string;
  description: string;
  data: Partial<CreateNotificationInput>;
}

const TEMPLATES: NotificationTemplate[] = [
  {
    id: 'comment-posted',
    name: 'Comment Posted',
    description: 'Someone posted a comment on your photo in a collection',
    data: {
      notificationType: 'CRITIQUE_POSTED',
      targetType: 'critique',
      targetId: 'cr1a',
      title: 'New comment on your photo',
      body: 'John Doe posted a comment on "Sunset at the Beach"',
      actionUrl: '/disc/co1c?photo=ph0a&comment=cr1a',
      urgency: 'medium',
      payload: {
        critiqueGlobalId: 'cr1a',
        photoGlobalId: 'ph0a',
        collectionGlobalId: 'co1c',
        photoTitle: 'Sunset at the Beach',
      },
    },
  },
  {
    id: 'comment-reply',
    name: 'Comment Reply',
    description: 'Someone replied to your comment',
    data: {
      notificationType: 'CRITIQUE_REPLY',
      targetType: 'critique',
      targetId: 'cr2b',
      title: 'Reply to your comment',
      body: 'Jane Smith replied to your comment on "Mountain Landscape"',
      actionUrl: '/disc/co1c?photo=ph0b&comment=cr2b',
      urgency: 'high',
      payload: {
        critiqueGlobalId: 'cr2b',
        photoGlobalId: 'ph0b',
        collectionGlobalId: 'co1c',
        parentCritiqueGlobalId: 'cr1a',
      },
    },
  },
  {
    id: 'photo-added',
    name: 'Photo Added to Collection',
    description: 'Your photo was added to a collection',
    data: {
      notificationType: 'PHOTO_ADDED_TO_COLLECTION',
      targetType: 'collection',
      targetId: 'co1c',
      title: 'Photo added to collection',
      body: 'Your photo "City Lights" was added to "Urban Photography"',
      actionUrl: '/c/co1c',
      urgency: 'low',
      payload: {
        photoId: 'ph0c',
        collectionName: 'Urban Photography',
      },
    },
  },
  {
    id: 'system-alert',
    name: 'System Alert',
    description: 'Important system notification',
    data: {
      notificationType: 'SYSTEM_ALERT',
      targetType: 'system',
      targetId: '0',
      title: 'System Maintenance Scheduled',
      body: 'We will be performing scheduled maintenance on Sunday at 2:00 AM UTC. Expected downtime: 30 minutes.',
      actionUrl: '/content/maintenance',
      urgency: 'high',
      payload: {
        maintenanceStart: '2025-11-17T02:00:00Z',
        maintenanceEnd: '2025-11-17T02:30:00Z',
      },
    },
  },
];

// ============================================================================
// Form Component
// ============================================================================

export function DevNotificationForm({ onSubmit, isSubmitting }: DevNotificationFormProps) {
  // Fetch users for recipient and actor dropdowns using GraphQL
  const { data: usersData, loading: isLoadingUsers } = useAdminUsersGraphQL(1, 100);

  const users = usersData?.users || [];

  // Form state
  const [recipientId, setRecipientId] = useState('');
  const [actorId, setActorId] = useState('');
  const [notificationType, setNotificationType] = useState<NotificationType>('CRITIQUE_POSTED');
  const [targetType, setTargetType] = useState('photo');
  const [targetId, setTargetId] = useState('ph0a');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [urgency, setUrgency] = useState<NotificationUrgency>('medium');
  const [payloadJson, setPayloadJson] = useState('{}');
  const [payloadError, setPayloadError] = useState<string | null>(null);

  // Template selection
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Set default recipient and actor when users are loaded
  useEffect(() => {
    if (users.length > 0 && !recipientId) {
      setRecipientId(users[0].globalId);
      if (users.length > 1) {
        setActorId(users[1].globalId);
      }
      logger.info('DevNotificationForm', 'Set default users', {
        recipientId: users[0].globalId,
        actorId: users.length > 1 ? users[1].globalId : 'none',
      });
    }
  }, [users, recipientId]);

  // Apply template when selected
  useEffect(() => {
    if (selectedTemplate) {
      const template = TEMPLATES.find((t) => t.id === selectedTemplate);
      if (template) {
        const data = template.data;
        if (data.notificationType) setNotificationType(data.notificationType as NotificationType);
        if (data.targetType) setTargetType(data.targetType);
        if (data.targetId) setTargetId(data.targetId);
        if (data.title) setTitle(data.title);
        if (data.body) setBody(data.body);
        if (data.actionUrl) setActionUrl(data.actionUrl);
        if (data.urgency) setUrgency(data.urgency);
        if (data.payload) {
          setPayloadJson(JSON.stringify(data.payload, null, 2));
          setPayloadError(null);
        }

        // Set recipient and actor from first available users
        if (users.length > 0) {
          setRecipientId(users[0].globalId);
          if (users.length > 1) {
            setActorId(users[1].globalId);
          }
          logger.info('DevNotificationForm', 'Applied template with users', {
            template: selectedTemplate,
            recipientId: users[0].globalId,
            actorId: users.length > 1 ? users[1].globalId : 'none',
          });
        }
      }
    }
  }, [selectedTemplate, users]);

  // Validate JSON payload
  const validatePayload = (json: string): boolean => {
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setPayloadError('Payload must be a JSON object');
        return false;
      }
      const jsonSize = new Blob([json]).size;
      if (jsonSize > 4096) {
        setPayloadError(`Payload too large: ${jsonSize} bytes (max 4KB)`);
        return false;
      }
      setPayloadError(null);
      return true;
    } catch (error) {
      setPayloadError('Invalid JSON');
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate payload
    if (!validatePayload(payloadJson)) {
      return;
    }

    const request: CreateNotificationInput = {
      recipientId,
      actorId: actorId || undefined,
      notificationType,
      targetType,
      targetId,
      title,
      body: body || undefined,
      actionUrl: actionUrl || undefined,
      urgency,
      payload: JSON.parse(payloadJson),
    };

    onSubmit(request);
  };

  // Character counts
  const titleLength = title.length;
  const bodyLength = body.length;
  const payloadSize = new Blob([payloadJson]).size;

  return (
    <form onSubmit={handleSubmit} className="bg-surface-1 border border-border rounded-lg p-6 space-y-6">
      {/* Template Selector */}
      <div>
        <label htmlFor="template" className="block text-sm font-medium text-foreground mb-2">
          Template (optional)
        </label>
        <select
          id="template"
          value={selectedTemplate}
          onChange={(e) => setSelectedTemplate(e.target.value)}
          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">-- Custom Notification --</option>
          {TEMPLATES.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name} - {template.description}
            </option>
          ))}
        </select>
      </div>

      {/* Recipient ID */}
      <div>
        <label htmlFor="recipientId" className="block text-sm font-medium text-foreground mb-2">
          Recipient <span className="text-danger">*</span>
        </label>
        {isLoadingUsers ? (
          <div className="w-full px-3 py-2 border border-border rounded-md bg-surface-2 text-muted">
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div className="w-full px-3 py-2 border border-border rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400">
            No users found. Please create a user first.
          </div>
        ) : (
          <select
            id="recipientId"
            value={recipientId}
            onChange={(e) => setRecipientId(e.target.value)}
            required
            className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">-- Select Recipient --</option>
            {users.map((user) => (
              <option key={user.globalId} value={user.globalId}>
                {user.globalId} - {user.username}
              </option>
            ))}
          </select>
        )}
        <p className="text-xs text-muted mt-1">User who will receive the notification</p>
      </div>

      {/* Actor ID */}
      <div>
        <label htmlFor="actorId" className="block text-sm font-medium text-foreground mb-2">
          Actor (optional)
        </label>
        {isLoadingUsers ? (
          <div className="w-full px-3 py-2 border border-border rounded-md bg-surface-2 text-muted">
            Loading users...
          </div>
        ) : (
          <select
            id="actorId"
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">-- None (System) --</option>
            {users.map((user) => (
              <option key={user.globalId} value={user.globalId}>
                {user.globalId} - {user.username}
              </option>
            ))}
          </select>
        )}
        <p className="text-xs text-muted mt-1">User who triggered the notification (leave blank for system)</p>
      </div>

      {/* Notification Type */}
      <div>
        <label htmlFor="notificationType" className="block text-sm font-medium text-foreground mb-2">
          Notification Type <span className="text-danger">*</span>
        </label>
        <select
          id="notificationType"
          value={notificationType}
          onChange={(e) => setNotificationType(e.target.value as NotificationType)}
          required
          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="CRITIQUE_POSTED">CRITIQUE_POSTED</option>
          <option value="CRITIQUE_REPLY">CRITIQUE_REPLY</option>
          <option value="PHOTO_ADDED_TO_COLLECTION">PHOTO_ADDED_TO_COLLECTION</option>
          <option value="COLLECTION_SHARED">COLLECTION_SHARED</option>
          <option value="SYSTEM_ALERT">SYSTEM_ALERT</option>
        </select>
      </div>

      {/* Target Type and ID */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="targetType" className="block text-sm font-medium text-foreground mb-2">
            Target Type <span className="text-danger">*</span>
          </label>
          <input
            id="targetType"
            type="text"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            required
            placeholder="photo"
            className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted mt-1">photo, discussion, collection</p>
        </div>

        <div>
          <label htmlFor="targetId" className="block text-sm font-medium text-foreground mb-2">
            Target ID <span className="text-danger">*</span>
          </label>
          <input
            id="targetId"
            type="text"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            required
            placeholder="ph0a"
            className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted mt-1">Global ID of target</p>
        </div>
      </div>

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
          Title <span className="text-danger">*</span>
          <span className="ml-2 text-xs font-normal text-muted">
            ({titleLength}/255 characters)
          </span>
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          maxLength={255}
          placeholder="New comment on your photo"
          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Body */}
      <div>
        <label htmlFor="body" className="block text-sm font-medium text-foreground mb-2">
          Body (optional)
          <span className="ml-2 text-xs font-normal text-muted">
            ({bodyLength} characters)
          </span>
        </label>
        <textarea
          id="body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Additional details about the notification..."
          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Action URL */}
      <div>
        <label htmlFor="actionUrl" className="block text-sm font-medium text-foreground mb-2">
          Action URL (optional)
        </label>
        <input
          id="actionUrl"
          type="text"
          value={actionUrl}
          onChange={(e) => setActionUrl(e.target.value)}
          placeholder="/p/ph0a"
          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="text-xs text-muted mt-1">URL to navigate to when notification is clicked</p>
      </div>

      {/* Urgency */}
      <div>
        <label htmlFor="urgency" className="block text-sm font-medium text-foreground mb-2">
          Urgency <span className="text-danger">*</span>
        </label>
        <select
          id="urgency"
          value={urgency}
          onChange={(e) => setUrgency(e.target.value as NotificationUrgency)}
          required
          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="low">Low (dropdown only)</option>
          <option value="medium">Medium (dropdown only)</option>
          <option value="high">High (triggers toast)</option>
        </select>
      </div>

      {/* Payload JSON */}
      <div>
        <label htmlFor="payload" className="block text-sm font-medium text-foreground mb-2">
          Payload (JSON object)
          <span className="ml-2 text-xs font-normal text-muted">
            ({payloadSize}/4096 bytes)
          </span>
        </label>
        <textarea
          id="payload"
          value={payloadJson}
          onChange={(e) => {
            setPayloadJson(e.target.value);
            validatePayload(e.target.value);
          }}
          rows={6}
          placeholder='{"key": "value"}'
          className={`w-full px-3 py-2 border rounded-md font-mono text-sm focus:outline-none focus:ring-2 ${
            payloadError
              ? 'border-danger focus:ring-danger'
              : 'border-border focus:ring-primary'
          }`}
        />
        {payloadError && (
          <p className="text-sm text-danger mt-1">{payloadError}</p>
        )}
      </div>

      {/* Submit Button */}
      <div>
        <button
          type="submit"
          disabled={isSubmitting || !!payloadError}
          className={`w-full px-4 py-2 rounded-md font-medium transition-colors ${
            isSubmitting || payloadError
              ? 'bg-surface-2 text-muted cursor-not-allowed'
              : 'bg-primary text-white hover:bg-primary/90'
          }`}
        >
          {isSubmitting ? 'Creating...' : 'Create Notification'}
        </button>
      </div>
    </form>
  );
}
